import { FormEvent, SetStateAction } from "react"

import { SelectChangeEvent } from "@mui/material/Select"
import { Theme } from "@mui/material/styles"

import { IAddOrUpdateMemberResponse } from "@nhi/newsletter/mailchimp/types"
import {
    INewsletterCard,
    INewsletterInterestsCategories,
    INewsletterMergeField
} from "data/contentData/interfaces/mongodb/INewsletter"
import { setNewsletterCookie } from "data/internal/api/newsletterCookie"
import { INewsletterSubscribe } from "data/mailchimp/interfaces/INewsletterSubscribe"
import { IDeviceDimensions } from "lib/hooks/deviceDimensions/IDeviceDimensions"
import { validateEmail } from "lib/validateEmail"

import { SelectOption } from "./NewsletterSelect"

export type FormInterest = {
    fieldTitle: string
    intererestsId: string
}

export type FormMergeFields = {
    fieldName: string
    value: string
}

export interface IFormValues {
    email: string
    listId: string
    interests: FormInterest[]
    merge_fields: FormMergeFields[]
}

// get the initial values for the form based on the newsletterId
export function getInitialFormValues(newsletterId: string) {
    return {
        email: "",
        listId: newsletterId,
        interests: [],
        merge_fields: []
    } as IFormValues
}

// check for categories in the card data
export function extractCategories(
    card: INewsletterCard
): INewsletterInterestsCategories[] {
    if (!card || !Array.isArray(card.categories)) return []

    const getFieldValue = (
        items: { name: string; value: any }[],
        key: string
    ) => items.find(f => f.name === key)?.value

    const mapInterest = (
        interestFields: { name: string; value: any }[]
    ): SelectOption => ({
        id: getFieldValue(interestFields, "id") ?? "",
        name: getFieldValue(interestFields, "name") ?? ""
    })

    return card.categories.map((fields: any) => {
        const interestsRaw = getFieldValue(fields, "interests")

        const interests = Array.isArray(interestsRaw)
            ? interestsRaw.map(mapInterest)
            : []

        return {
            category_id: getFieldValue(fields, "category_id") ?? "",
            title: getFieldValue(fields, "title") ?? "",
            type: getFieldValue(fields, "type") ?? "",
            required: Boolean(getFieldValue(fields, "required")),
            interests
        } as INewsletterInterestsCategories
    })
}

// check for merge fields in the card data
export function extractMergeFields(
    card: INewsletterCard
): INewsletterMergeField[] {
    if (!card?.mergeFields) return []

    if (Array.isArray(card.mergeFields))
        return card.mergeFields.map((group: any[]) => {
            const map = group.reduce<Record<string, any>>((acc, item) => {
                acc[item.name] = item.value
                return acc
            }, {})

            return {
                tag: map.tag ?? "",
                name: map.name ?? "",
                type: map.type ?? "",
                required: Boolean(map.required),
                default_value: map.default_value ?? "",
                options: Array.isArray(map.options) ? map.options : []
            } as INewsletterMergeField
        })

    return []
}

// update the form state for interests and mergefields
export function handleSelectChange(
    event: SelectChangeEvent,
    type: string,
    form: IFormValues,
    setForm: (value: SetStateAction<IFormValues>) => void,
    tag?: string
) {
    if (type === "interests") {
        const prevState = [...form.interests]

        const existingFieldIndex = prevState.findIndex(
            item => item.fieldTitle === event.target.name
        )
        if (existingFieldIndex !== -1)
            prevState[existingFieldIndex].intererestsId = event.target.value
        else
            prevState.push({
                fieldTitle: event.target.name,
                intererestsId: event.target.value
            })

        setForm({ ...form, interests: prevState })
    } else if (type === "mergefields" && tag) {
        const prevState = [...form.merge_fields]

        const existingFieldIndex = prevState.findIndex(
            item => item.fieldName === tag
        )
        if (existingFieldIndex !== -1)
            prevState[existingFieldIndex].value = event.target.value
        else
            prevState.push({
                fieldName: tag,
                value: event.target.value
            })

        setForm({ ...form, merge_fields: prevState })
    }
}

// update the form  state for email
export function handleEmailChange(
    input: string,
    form: IFormValues,
    setForm: (value: SetStateAction<IFormValues>) => void,
    setEmailError: (value: SetStateAction<boolean>) => void
) {
    const inputValue = input
    setForm({
        ...form,
        email: inputValue
    })
    if (!validateEmail(inputValue)) setEmailError(true)
    else setEmailError(false)
}

// handle the sign up form submission
export async function handleSignUp(
    newsletterId: string,
    formId: string,
    e: FormEvent<HTMLDivElement>,
    emailError: boolean,
    form: IFormValues,
    setIsSending: (value: SetStateAction<boolean>) => void,
    setSuccessfullySubscribed: (
        value: SetStateAction<boolean | undefined>
    ) => void,
    setNewsletterSubscription: (
        value: INewsletterSubscribe
    ) => Promise<IAddOrUpdateMemberResponse>
) {
    e.preventDefault()
    if (emailError) return

    const formattedInterests = getFormattedInterests(form)
    const formattedMergefields = getFormattedMergefields(form)

    setIsSending(true)
    const res = await setNewsletterSubscription({
        email: form.email,
        newsletterId: form.listId,
        interests: formattedInterests,
        merge_fields: formattedMergefields
    } as INewsletterSubscribe)

    setIsSending(false)
    if (res.status !== "subscribed") setSuccessfullySubscribed(false)
    else {
        setSuccessfullySubscribed(true)
        // set cookie to avoid showing popup newsletters if user is subscribed
        setNewsletterCookie(formId, newsletterId)
    }
}

// clear all fields in the form
export function clearAllFields(
    setForm: (value: SetStateAction<IFormValues>) => void,
    initialFormValues: IFormValues,
    setEmailError: (value: SetStateAction<boolean>) => void,
    setSuccessfullySubscribed: (
        value: SetStateAction<boolean | undefined>
    ) => void
) {
    setForm(initialFormValues)
    setEmailError(false)
    setSuccessfullySubscribed(undefined)
}

// get the render value for the interest fields
export function getInterestRenderValue(title: string, form: IFormValues) {
    const existingField = form.interests.find(item => item.fieldTitle === title)
    return existingField ? existingField.intererestsId : ""
}

// get the render value for the mergefields
export function getMergefieldsRenderValue(tag: string, form: IFormValues) {
    const selectedValue = form.merge_fields.find(item => item.fieldName === tag)
    return selectedValue ? selectedValue.value : ""
}

// list of mergefields from state converted to object acceptable to mailchimp api
function getFormattedInterests(form: IFormValues) {
    const interestsState = form.interests.reduce((acc, curr) => {
        acc[curr.intererestsId as string] = true
        return acc
    }, {} as { [key: string]: boolean })
    return interestsState
}

// list of mergefields from state converted to object acceptable to mailchimp api
function getFormattedMergefields(form: IFormValues) {
    const mergeFieldsState = form.merge_fields.reduce((acc, curr) => {
        acc[curr.fieldName] = curr.value
        return acc
    }, {} as { [key: string]: string })
    return mergeFieldsState
}

// get the correct palette based on the newsletterId
export function getNewsletterPalette(formId: string, theme: Theme) {
    let palette
    formId === "newsletter-pregnancy"
        ? (palette = theme.palette.pregnancy)
        : (palette = theme.palette.newsletter)

    return palette
}

// device dimensions for the newsletter component used to render scelton loader
export const newsletterDeviceDimensions: IDeviceDimensions = {
    sm: {
        height: 400
    },
    md: {
        height: 250
    },
    lg: {
        height: 200
    }
}
